import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Landmark, ArrowRight, X, Copy, CalendarClock, AlertTriangle, Info } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money } from "../context/PrivacyContext";
import { fmtDate, fmtEUR } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import MoneyMovementFields from "../components/MoneyMovementFields";
import ConceptSelect from "../components/ConceptSelect";
import MemberSelect, { MemberBadge } from "../components/MemberSelect";
import MovementTrace from "../components/MovementTrace";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import ProviderSelect from "../components/ProviderSelect";

const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;

const TYPES = [
  { key: "fijo", label: "Fijo", desc: "Mismo importe, misma periodicidad (hipoteca, parking, seguros)" },
  { key: "variable", label: "Variable", desc: "Periodicidad fija, importe cambia (luz, agua, gas, internet)" },
  { key: "estacional", label: "Estacional", desc: "Fechas concretas del año (material escolar, campamentos)" },
  { key: "programado", label: "Programado", desc: "Gastos futuros conocidos (ITV, IBI, vacaciones)" },
];

const PERIODS = [["mensual", "Mensual"], ["bimestral", "Bimestral"], ["trimestral", "Trimestral"], ["anual", "Anual"]];
const STATUSES = [["activo", "Activo"], ["pausado", "Pausado"], ["finalizado", "Finalizado"]];
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const EMPTY = { name: "", provider_id: null, description: "", type: "fijo", category: "Vivienda", concept_id: null, member_id: null, amount: "", day_of_month: "1", periodicity: "mensual", anchor_month: "1", schedule: [], start_date: "", end_date: "", status: "activo", account_id: null, payment_method_id: null };

function ScheduleEditor({ type, schedule, setSchedule }) {
  const add = () => setSchedule([...schedule, type === "estacional" ? { month: 9, day: 1, amount: "" } : { date: "", amount: "" }]);
  const upd = (i, k, v) => setSchedule(schedule.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const del = (i) => setSchedule(schedule.filter((_, j) => j !== i));
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground">{type === "estacional" ? "Fechas del año e importes" : "Fechas futuras e importes previstos"}</div>
      {schedule.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          {type === "estacional" ? (
            <>
              <Select value={String(s.month)} onValueChange={(v) => upd(i, "month", parseInt(v))}>
                <SelectTrigger className="flex-1" data-testid={`schedule-month-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, j) => <SelectItem key={j} value={String(j + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid={`schedule-day-${i}`} className="w-16" inputMode="numeric" placeholder="Día" value={s.day} onChange={(e) => upd(i, "day", parseInt(e.target.value.replace(/\D/g, "")) || 1)} />
            </>
          ) : (
            <Input data-testid={`schedule-date-${i}`} type="date" className="flex-1" value={s.date} onChange={(e) => upd(i, "date", e.target.value)} />
          )}
          <Input data-testid={`schedule-amount-${i}`} className="w-24" inputMode="decimal" placeholder="€" value={s.amount} onChange={(e) => upd(i, "amount", e.target.value)} />
          <button data-testid={`schedule-remove-${i}`} onClick={() => del(i)} className="text-muted-foreground hover:text-red-600"><X size={15} /></button>
        </div>
      ))}
      <button data-testid="schedule-add-button" onClick={add} className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
        <Plus size={13} /> Añadir fecha
      </button>
    </div>
  );
}

function CommitmentRow({ c, onEdit, onDelete, onDuplicate }) {
  const infoText = () => {
    if (c.type === "variable") {
      const src = c.forecast_source || {};
      if (src.origen === "gastos") {
        return `Previsión calculada automáticamente con la media de tus últimos ${src.n} gasto${src.n > 1 ? "s" : ""} reales de este proveedor (último: ${fmtDate(src.ultimo)}). Registra cada factura en Gastos y la media se actualiza sola.`;
      }
      return "Se alimenta de tus gastos reales: registra las facturas de este proveedor en Gastos y la previsión media se calculará automáticamente. Mientras no haya gastos, se usa el importe manual.";
    }
    if (c.type === "fijo") return "Importe fijo (alquiler, hipoteca, cuotas pactadas…). No es necesario volcar datos en Gastos: la previsión es siempre el importe acordado.";
    if (c.type === "estacional") return "Importes definidos por calendario (fechas concretas del año). La previsión suma los tramos que configures aquí; no depende de Gastos.";
    return "Pagos en fechas programadas. La previsión usa el importe de cada vencimiento configurado; no depende de Gastos.";
  };
  const sub = () => {
    if (c.type === "fijo") return `Día ${c.day_of_month} · ${c.periodicity} · ${c.category}`;
    if (c.type === "variable") return `Día ${c.day_of_month} · ${c.periodicity} · media últimos importes`;
    if (c.type === "estacional") return (c.schedule || []).map((s) => `${s.day} ${MONTHS[(s.month || 1) - 1].slice(0, 3)}`).join(", ") || "Sin fechas";
    return (c.schedule || []).map((s) => fmtDate(s.date)).join(", ") || "Sin fechas";
  };
  const total = c.type === "estacional" || c.type === "programado" ? (c.schedule || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) : c.forecast_amount;
  const status = c.status || "activo";
  return (
    <div className={`flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors ${status !== "activo" ? "opacity-60" : ""}`} data-testid={`commitment-row-${c.id}`}>
      <div className="min-w-0">
        <div className="font-medium text-sm truncate flex items-center gap-2">
          {c.provider_name || c.name}
          <MemberBadge memberId={c.member_id} />
          {status !== "activo" && (
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${status === "pausado" ? "bg-amber-100 text-amber-700" : "bg-secondary text-muted-foreground"}`}>{status}</span>
          )}
        </div>
        {c.description && <div className="text-xs text-muted-foreground truncate italic">{c.description}</div>}
        <div className="text-xs text-muted-foreground truncate">{sub()}</div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <Money value={total} className="block font-semibold tabular" />
          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
            {c.type === "variable" && ((c.forecast_source || {}).origen === "gastos" ? `media de ${c.forecast_source.n} gastos reales` : "previsión media")}
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="Cómo se calcula" data-testid={`commitment-info-${c.id}`} className="text-muted-foreground/70 hover:text-foreground transition-colors align-middle">
                    <Info size={12} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[280px] text-xs leading-relaxed">{infoText()}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </span>
        </div>
        <button data-testid={`duplicate-commitment-${c.id}`} onClick={onDuplicate} title="Duplicar" className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
        <button data-testid={`edit-commitment-${c.id}`} onClick={onEdit} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
        <button data-testid={`delete-commitment-${c.id}`} onClick={onDelete} className="text-muted-foreground hover:text-red-600"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

export default function Compromisos() {
  const [items, setItems] = useState(null);
  const [fins, setFins] = useState([]);
  const [insurances, setInsurances] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [cats, setCats] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [period, setPeriod] = useState("month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [pending, setPending] = useState([]);
  const [incomesPending, setIncomesPending] = useState({ items: [], by_account: [], total: 0 });
  const [simCount, setSimCount] = useState(0);
  const [showCobros, setShowCobros] = useState(false);

  const load = () => {
    api.get("/commitments").then((r) => setItems(r.data));
    api.get("/financings").then((r) => setFins(r.data.filter((f) => f.active)));
    api.get("/insurances").then((r) => setInsurances(r.data.filter((i) => (i.status || "activo") === "activo")));
  };
  useEffect(() => {
    load();
    api.get("/categories?kind=gasto").then((r) => setCats(r.data.filter((c) => !c.parent_id && c.active).map((c) => c.name)));
    api.get("/assets").then((r) => setAccounts(r.data));
  }, []);

  // Rango del periodo seleccionado
  const [rangeFrom, rangeTo, rangeLabel] = (() => {
    const t = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (period === "day") return [iso(t), iso(t), "Hoy"];
    if (period === "week") {
      const end = new Date(t); end.setDate(end.getDate() + 6);
      return [iso(t), iso(end), "Próximos 7 días"];
    }
    if (period === "fortnight") {
      const end = new Date(t); end.setDate(end.getDate() + 14);
      return [iso(t), iso(end), "Próximos 15 días"];
    }
    if (period === "month") {
      const end = new Date(t); end.setMonth(end.getMonth() + 1); end.setDate(end.getDate() - 1);
      return [iso(t), iso(end), "Próximos 30 días"];
    }
    if (period === "range" && customFrom && customTo) return [customFrom, customTo, `${customFrom} → ${customTo}`];
    return [iso(t), iso(t), "Selecciona rango"];
  })();

  useEffect(() => {
    if (!rangeFrom || !rangeTo) return;
    api.get(`/payments/pending?desde=${rangeFrom}&hasta=${rangeTo}`).then((r) => setPending(r.data.items || [])).catch(() => setPending([]));
    api.get(`/incomes/pending?desde=${rangeFrom}&hasta=${rangeTo}`).then((r) => setIncomesPending(r.data)).catch(() => setIncomesPending({ items: [], by_account: [], total: 0 }));
    api.get("/incomes/simulated/count").then((r) => setSimCount(r.data.count)).catch(() => setSimCount(0));
  }, [rangeFrom, rangeTo]);

  const submit = async () => {
    try {
      // V1.41: validaciones cliente
      if (!form.provider_id) { toast.error("Selecciona un acreedor/proveedor del catálogo"); return; }
      if (!form.description || !form.description.trim()) { toast.error("Descripción/Concepto es obligatoria"); return; }
      const payload = {
        name: form.name || form.description.slice(0, 60),  // fallback compat backend
        provider_id: form.provider_id,
        description: form.description,
        type: form.type,
        category: form.category,
        concept_id: form.concept_id || null,
        member_id: form.member_id || null,
        amount: num(form.amount),
        day_of_month: parseInt(form.day_of_month) || 1,
        periodicity: form.periodicity,
        anchor_month: parseInt(form.anchor_month) || 1,
        schedule: form.schedule.map((s) => ({ ...s, amount: num(s.amount) })),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: form.status || "activo",
        account_id: form.account_id,
        payment_method_id: form.payment_method_id,
      };
      if (editId) await api.put(`/commitments/${editId}`, payload);
      else await api.post("/commitments", payload);
      toast.success(editId ? "Compromiso actualizado" : "Compromiso creado");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (id) => { await api.delete(`/commitments/${id}`); load(); };

  const toForm = (c) => ({ name: c.name, provider_id: c.provider_id || null, description: c.description || "", type: c.type, category: c.category, concept_id: c.concept_id || null, member_id: c.member_id || null, amount: c.amount, day_of_month: String(c.day_of_month), periodicity: c.periodicity, anchor_month: String(c.anchor_month || 1), schedule: c.schedule || [], start_date: c.start_date || "", end_date: c.end_date || "", status: c.status || "activo", account_id: c.account_id || null, payment_method_id: c.payment_method_id || null });

  const startEdit = (c) => {
    setEditDoc(c);
    setForm(toForm(c));
    setEditId(c.id);
    setOpen(true);
  };

  const duplicateItem = (c) => {
    setForm({ ...toForm(c), name: `${c.name} (copia)` });
    setEditId(null);
    setOpen(true);
    toast.info("Datos copiados. Ajusta lo que necesites y guarda");
  };

  const insMonthly = insurances.reduce((s, i) => {
    const receipts = i.receipts || [];
    if (receipts.length) return s + receipts.reduce((a, r) => a + (r.amount || 0), 0) / 12;
    return s + (i.annual_premium || 0) / 12;
  }, 0);
  const monthlyEq = (items || []).reduce((s, c) => s + c.monthly_equivalent, 0) + fins.reduce((s, f) => s + f.monthly_payment, 0) + insMonthly;
  const rangeTotal = pending.reduce((s, p) => s + (p.amount || 0), 0);
  const rangeByType = pending.reduce((acc, p) => { acc[p.type] = (acc[p.type] || 0) + (p.amount || 0); return acc; }, {});
  const needsSchedule = form.type === "estacional" || form.type === "programado";
  const valid = form.provider_id && form.description && form.description.trim() && (needsSchedule ? form.schedule.length > 0 : form.amount !== "");

  return (
    <div className="space-y-6" data-testid="compromisos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Compromisos financieros</h1>
          <p className="text-sm text-muted-foreground mt-1">Fijos, variables, estacionales, programados y financiaciones. Todo alimenta tus previsiones.</p>
        </div>
        <Button data-testid="add-commitment-button" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}>
          <Plus size={16} className="mr-1" /> Añadir compromiso
        </Button>
      </div>

      {simCount > 0 && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="sim-warning">
          <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <b className="text-amber-800 dark:text-amber-300">Existen {simCount} ingreso{simCount === 1 ? "" : "s"} simulado{simCount === 1 ? "" : "s"} activo{simCount === 1 ? "" : "s"} que afectan a los cálculos mostrados.</b>
        </div>
      )}

      <div className="border border-border rounded-lg p-5 space-y-4" data-testid="compromisos-summary">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold">Equivalente mensual total</div>
            <Money value={monthlyEq} testId="total-compromisos" className="block mt-1 text-3xl font-semibold tabular" />
            <p className="text-xs text-muted-foreground mt-1">Incluye compromisos (prorrateados), financiaciones activas y seguros activos.</p>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1 justify-end"><CalendarClock size={11} /> Pagos en {rangeLabel.toLowerCase()}</div>
            <Money value={rangeTotal} testId="range-total" className="block mt-1 text-3xl font-semibold tabular" />
            <p className="text-xs text-muted-foreground mt-1">{pending.length} vencimiento{pending.length === 1 ? "" : "s"} en el rango.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          {[["day", "Día"], ["week", "Semana"], ["fortnight", "Quincena"], ["month", "Mes"], ["range", "Rango"]].map(([k, l]) => (
            <button
              key={k}
              data-testid={`period-${k}`}
              onClick={() => setPeriod(k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${period === k ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-secondary/70"}`}
            >
              {l}
            </button>
          ))}
          {period === "range" && (
            <div className="flex items-center gap-2 ml-2">
              <Input data-testid="range-from" type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-8 w-40" />
              <span className="text-xs text-muted-foreground">→</span>
              <Input data-testid="range-to" type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-8 w-40" />
            </div>
          )}
        </div>

        {pending.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {Object.entries(rangeByType).map(([type, total]) => (
              <div key={type} className="bg-secondary/50 rounded-lg px-3 py-2 flex items-center justify-between" data-testid={`range-type-${type}`}>
                <span className="capitalize text-muted-foreground">{type}</span>
                <Money value={total} className="font-semibold tabular" />
              </div>
            ))}
          </div>
        )}

        {pending.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer font-semibold text-xs uppercase tracking-wider text-muted-foreground">Ver {pending.length} vencimiento{pending.length === 1 ? "" : "s"} en detalle</summary>
            <div className="mt-2 max-h-64 overflow-y-auto divide-y divide-border border border-border rounded" data-testid="range-detail-list">
              {pending.slice(0, 100).map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.name}</div>
                    <div className="text-muted-foreground">{fmtDate(p.date)} · <span className="capitalize">{p.type}</span></div>
                  </div>
                  <Money value={p.amount} className="font-semibold tabular shrink-0" />
                </div>
              ))}
            </div>
          </details>
        )}

        {incomesPending.total > 0 && (
          <div className="border-t border-border pt-3" data-testid="cobros-block">
            <button onClick={() => setShowCobros((v) => !v)} className="w-full flex items-center justify-between text-left">
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400">Cobros previstos · {rangeLabel.toLowerCase()}</div>
                <div className="text-lg font-semibold tabular mt-0.5">{fmtEUR(incomesPending.total)} <span className="text-xs text-muted-foreground">· {incomesPending.count} cobro{incomesPending.count === 1 ? "" : "s"}</span></div>
              </div>
              <span className="text-xs text-muted-foreground">{showCobros ? "Ocultar" : "Ver detalle"}</span>
            </button>
            <div className="mt-2 space-y-1">
              {incomesPending.by_account.map((row) => (
                <div key={row.account_id || "none"} className="flex items-center justify-between text-sm py-1 border-b border-border/40 last:border-0" data-testid={`cobro-account-${row.account_id || "none"}`}>
                  <span className="truncate">{row.account_name || "Sin cuenta"} <span className="text-xs text-muted-foreground">({row.count})</span></span>
                  <Money value={row.total} className="font-semibold tabular" />
                </div>
              ))}
              <div className="flex items-center justify-between text-sm pt-2 font-bold">
                <span>TOTAL</span>
                <Money value={incomesPending.total} className="tabular" />
              </div>
            </div>
            {showCobros && (
              <div className="mt-3 max-h-64 overflow-y-auto divide-y divide-border border border-border rounded" data-testid="cobros-detail">
                {incomesPending.items.slice(0, 200).map((p, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-xs">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.concept}{p.simulated && <span className="ml-2 text-[9px] uppercase bg-amber-200 text-amber-900 px-1 rounded font-bold">Simulado</span>}</div>
                      <div className="text-muted-foreground">{fmtDate(p.date)} · {p.account_name || "Sin cuenta"}</div>
                    </div>
                    <Money value={p.amount} className="font-semibold tabular shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <PaymentsCalendar from={rangeFrom} to={rangeTo} items={pending} label={rangeLabel} />

      {items === null ? (
        <div className="text-sm text-muted-foreground">Cargando…</div>
      ) : (
        TYPES.map((t) => {
          const list = items.filter((c) => c.type === t.key);
          return (
            <div key={t.key} data-testid={`section-${t.key}`}>
              <h3 className="font-heading font-bold text-lg">{t.label} ({list.length})</h3>
              <p className="text-xs text-muted-foreground mb-2">{t.desc}</p>
              <div className="border border-border rounded-lg divide-y divide-border">
                {list.length === 0 ? (
                  <div className="p-4 text-sm text-muted-foreground">Sin compromisos de este tipo.</div>
                ) : (
                  list.map((c) => <CommitmentRow key={c.id} c={c} onEdit={() => startEdit(c)} onDelete={() => remove(c.id)} onDuplicate={() => duplicateItem(c)} />)
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Financiaciones */}
      <div data-testid="section-financiacion">
        <h3 className="font-heading font-bold text-lg">Financiación ({fins.length})</h3>
        <p className="text-xs text-muted-foreground mb-2">Móvil, TV, electrodomésticos, vehículos… con cálculo de cuotas, capital pendiente y cancelación.</p>
        <Link to="/financiaciones" data-testid="link-financiaciones-module" className="flex items-center justify-between border border-border rounded-lg p-4 hover:bg-secondary/50 transition-colors">
          <div className="flex items-center gap-3">
            <Landmark size={18} />
            <div>
              <div className="text-sm font-medium">{fins.length} financiaciones activas</div>
              <div className="text-xs text-muted-foreground">Gestión completa en su módulo</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Money value={fins.reduce((s, f) => s + f.monthly_payment, 0)} className="font-semibold tabular" />
            <span className="text-xs text-muted-foreground">/mes</span>
            <ArrowRight size={15} className="text-muted-foreground" />
          </div>
        </Link>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Editar compromiso" : "Nuevo compromiso"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2" data-testid="commitment-type-chips">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`type-chip-${t.key}`}
                  onClick={() => setForm({ ...form, type: t.key, schedule: [] })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${form.type === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{TYPES.find((t) => t.key === form.type)?.desc}</p>
            <div>
              <label className="text-xs text-muted-foreground">Acreedor / Proveedor *</label>
              <ProviderSelect
                testId="commitment-provider-select"
                value={form.provider_id}
                onChange={(id) => setForm({ ...form, provider_id: id })}
                legacyName={!form.provider_id ? form.name : ""}
                required
                placeholder="Selecciona un acreedor del catálogo…"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Descripción / Concepto *</label>
              <Textarea
                data-testid="commitment-description-input"
                placeholder="Ej. Modelo 303 IVA 3T 2026 · IBI oficina · Impuesto circulación Nissan Qashqai"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Concepto (Catálogo Maestro)</label>
              <ConceptSelect
                kind="compromiso"
                value={form.concept_id}
                legacyLabel={!form.concept_id ? form.category : ""}
                onChange={(id) => setForm({ ...form, concept_id: id })}
                testId="commitment-concept-select"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Pertenece a</label>
              <MemberSelect value={form.member_id} onChange={(id) => setForm({ ...form, member_id: id })} preselectCurrent={!editId} testId="commitment-member-select" />
            </div>

            {(form.type === "fijo" || form.type === "variable") && (
              <>
                <Input data-testid="commitment-amount-input" placeholder={form.type === "variable" ? "Importe actual (€)" : "Importe (€)"} inputMode="decimal" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground">Periodicidad</label>
                    <Select value={form.periodicity} onValueChange={(v) => setForm({ ...form, periodicity: v })}>
                      <SelectTrigger data-testid="commitment-periodicity-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PERIODS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Día de cobro</label>
                    <Input data-testid="commitment-day-input" inputMode="numeric" value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value.replace(/\D/g, "") })} />
                  </div>
                </div>
                {form.periodicity !== "mensual" && (
                  <div>
                    <label className="text-xs text-muted-foreground">Mes de referencia (primer cobro del ciclo)</label>
                    <Select value={form.anchor_month} onValueChange={(v) => setForm({ ...form, anchor_month: v })}>
                      <SelectTrigger data-testid="commitment-anchor-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{MONTHS.map((m, j) => <SelectItem key={j} value={String(j + 1)}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {form.type === "variable" && (
                  <p className="text-xs text-muted-foreground">Actualiza el importe con cada factura: la previsión usará la media de los últimos importes automáticamente.</p>
                )}
              </>
            )}

            {needsSchedule && <ScheduleEditor type={form.type} schedule={form.schedule} setSchedule={(s) => setForm({ ...form, schedule: s })} />}

            {(form.type === "fijo" || form.type === "variable" || form.type === "estacional") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Fecha de inicio (opcional)</label>
                  <Input data-testid="commitment-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Fecha de fin (opcional)</label>
                  <Input data-testid="commitment-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground -mt-1">No se incluirá en previsiones hasta su fecha de inicio. Ideal para simular gastos futuros que aún no existen.</p>
              </div>
            )}

            <div>
              <label className="text-xs text-muted-foreground">Estado</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="commitment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <MoneyMovementFields direction="out" values={form} onChange={(patch) => setForm({ ...form, ...patch })} testPrefix="commitment" />
            {form.status !== "activo" && <p className="text-xs text-amber-600">Los compromisos pausados o finalizados no se incluyen en previsiones ni pagos pendientes.</p>}

            <Button data-testid="commitment-submit-button" onClick={submit} disabled={!valid} className="w-full">
              {editId ? "Guardar cambios" : "Crear compromiso"}
            </Button>
            {editId && <MovementTrace doc={editDoc} />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const WD = ["L", "M", "X", "J", "V", "S", "D"];

function PaymentsCalendar({ from, to, items, label }) {
  if (!from || !to) return null;
  const d1 = new Date(from + "T00:00:00");
  const d2 = new Date(to + "T00:00:00");
  const diffDays = Math.round((d2 - d1) / 86400000) + 1;
  if (diffDays <= 0 || diffDays > 400) return null;

  // Agrupar importes por día
  const byDay = {};
  items.forEach((p) => { byDay[p.date] = (byDay[p.date] || []); byDay[p.date].push(p); });
  const maxDay = Math.max(1, ...Object.values(byDay).map((arr) => arr.reduce((s, x) => s + (x.amount || 0), 0)));

  // Construir grid: si el rango es > 45 días agrupamos por semanas (heatmap semanal), si no por días
  const groupBy = diffDays > 45 ? "week" : "day";
  const cells = [];
  if (groupBy === "day") {
    // Alinear al lunes de la semana del d1
    const start = new Date(d1);
    const dow = (start.getDay() + 6) % 7; // 0=Lun
    start.setDate(start.getDate() - dow);
    const end = new Date(d2);
    const endDow = (end.getDay() + 6) % 7;
    end.setDate(end.getDate() + (6 - endDow));
    for (let t = new Date(start); t <= end; t.setDate(t.getDate() + 1)) {
      const iso = t.toISOString().slice(0, 10);
      const inRange = t >= d1 && t <= d2;
      const dayItems = byDay[iso] || [];
      const total = dayItems.reduce((s, x) => s + (x.amount || 0), 0);
      cells.push({ iso, day: t.getDate(), month: t.getMonth(), total, count: dayItems.length, inRange, items: dayItems });
    }
  } else {
    // Semanas: agrupar cada 7 días desde d1
    for (let t = new Date(d1); t <= d2; t.setDate(t.getDate() + 7)) {
      const wStart = new Date(t);
      const wEnd = new Date(t); wEnd.setDate(wEnd.getDate() + 6);
      if (wEnd > d2) wEnd.setTime(d2.getTime());
      let total = 0, count = 0, weekItems = [];
      for (let d = new Date(wStart); d <= wEnd; d.setDate(d.getDate() + 1)) {
        const arr = byDay[d.toISOString().slice(0, 10)] || [];
        total += arr.reduce((s, x) => s + (x.amount || 0), 0);
        count += arr.length;
        weekItems = weekItems.concat(arr);
      }
      cells.push({ iso: wStart.toISOString().slice(0, 10), label: `${wStart.getDate()}–${wEnd.getDate()}/${wEnd.getMonth() + 1}`, total, count, inRange: true, items: weekItems, week: true });
    }
  }
  const maxCell = Math.max(1, ...cells.map((c) => c.total));

  const intensity = (v) => {
    if (v === 0) return "bg-secondary/40";
    const ratio = v / maxCell;
    if (ratio < 0.15) return "bg-primary/20";
    if (ratio < 0.35) return "bg-primary/40";
    if (ratio < 0.6) return "bg-primary/60";
    if (ratio < 0.85) return "bg-primary/80";
    return "bg-primary";
  };

  return (
    <div className="border border-border rounded-lg p-5" data-testid="payments-calendar">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-bold text-sm uppercase tracking-wider">Calendario de vencimientos</h3>
          <p className="text-xs text-muted-foreground">{label} · intensidad = concentración de pagos</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <span>menos</span>
          <span className="w-3 h-3 rounded bg-secondary/40 border border-border" />
          <span className="w-3 h-3 rounded bg-primary/20" />
          <span className="w-3 h-3 rounded bg-primary/40" />
          <span className="w-3 h-3 rounded bg-primary/60" />
          <span className="w-3 h-3 rounded bg-primary/80" />
          <span className="w-3 h-3 rounded bg-primary" />
          <span>más</span>
        </div>
      </div>

      {groupBy === "day" ? (
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WD.map((d) => <div key={d} className="text-center text-[10px] font-bold uppercase text-muted-foreground">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => (
              <div
                key={i}
                data-testid={`calendar-day-${c.iso}`}
                title={c.total > 0 ? `${c.iso} · ${c.count} pago(s) · ${fmtEUR(c.total)}` : c.iso}
                className={`aspect-square rounded flex flex-col items-center justify-center transition-transform hover:scale-105 ${c.inRange ? intensity(c.total) : "opacity-30 bg-secondary/20"} ${c.total > 0 ? "cursor-help" : ""}`}
              >
                <div className={`text-[10px] font-semibold ${c.total > maxCell * 0.5 ? "text-primary-foreground" : ""}`}>{c.day}</div>
                {c.total > 0 && <div className={`text-[8px] tabular font-bold ${c.total > maxCell * 0.5 ? "text-primary-foreground" : "text-foreground/80"}`}>{Math.round(c.total)}€</div>}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
          {cells.map((c, i) => (
            <div
              key={i}
              data-testid={`calendar-week-${c.iso}`}
              title={c.count > 0 ? `Semana ${c.label} · ${c.count} pago(s) · ${fmtEUR(c.total)}` : c.label}
              className={`rounded p-2 ${intensity(c.total)} ${c.total > 0 ? "cursor-help" : ""}`}
            >
              <div className={`text-[10px] font-semibold ${c.total > maxCell * 0.5 ? "text-primary-foreground" : ""}`}>Sem. {c.label}</div>
              <div className={`text-sm font-bold tabular ${c.total > maxCell * 0.5 ? "text-primary-foreground" : ""}`}>{fmtEUR(c.total)}</div>
              <div className={`text-[9px] ${c.total > maxCell * 0.5 ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{c.count} pago{c.count === 1 ? "" : "s"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

